// useAuth - Authentication hook with Firebase + localStorage favorites/bookmarks
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  User as FirebaseUser,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile,
} from '@firebase/auth';
import { doc, setDoc, getDoc } from '@firebase/firestore';
import { auth, db } from '../lib/firebase';

export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  favorites: number[];
  bookmarks: number[];
  watchHistory: ContinueWatchItem[];
  createdAt: number;
}

export interface ContinueWatchItem {
  id: number;
  title: string;
  poster: string;
  type: string;
  episode: number;
  timestamp: number;
  duration?: number;
  progress?: number;
}

interface AuthContextType {
  user: FirebaseUser | null;
  profile: UserProfile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (data: Partial<{ displayName: string; photoURL: string }>) => Promise<void>;
  toggleFavorite: (animeId: number) => void;
  toggleBookmark: (animeId: number) => void;
  favoriteExists: (animeId: number) => boolean;
  bookmarkExists: (animeId: number) => boolean;
  updateWatchProgress: (item: Omit<ContinueWatchItem, 'timestamp'>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // Return a defaults (guest mode) so components don't crash
    return {
      user: null,
      profile: null,
      loading: false,
      login: async () => {},
      register: async () => {},
      logout: async () => {},
      updateProfile: async () => {},
      toggleFavorite: () => {},
      toggleBookmark: () => {},
      favoriteExists: () => false,
      bookmarkExists: () => false,
      updateWatchProgress: () => {},
    } as AuthContextType;
  }
  return context;
};

// Firestore keys per user
const getFirestoreKey = (uid: string) => `user_profile_${uid}`;

// Local storage defaults for guests (no Firebase needed)
const LOCAL_FAVORITES_KEY = 'anime_favorites';
const LOCAL_BOOKMARKS_KEY = 'anime_bookmarks';
const LOCAL_WATCH_KEY = 'anime_continue_watching';

function getLocalFavorites(): number[] {
  try { return JSON.parse(localStorage.getItem(LOCAL_FAVORITES_KEY) || '[]'); } catch { return []; }
}
function saveLocalFavorites(list: number[]) {
  localStorage.setItem(LOCAL_FAVORITES_KEY, JSON.stringify(list));
}
function getLocalBookmarks(): number[] {
  try { return JSON.parse(localStorage.getItem(LOCAL_BOOKMARKS_KEY) || '[]'); } catch { return []; }
}
function saveLocalBookmarks(list: number[]) {
  localStorage.setItem(LOCAL_BOOKMARKS_KEY, JSON.stringify(list));
}
function getLocalWatch(): ContinueWatchItem[] {
  try { return JSON.parse(localStorage.getItem(LOCAL_WATCH_KEY) || '[]'); } catch { return []; }
}
function saveLocalWatch(list: ContinueWatchItem[]) {
  localStorage.setItem(LOCAL_WATCH_KEY, JSON.stringify(list.slice(0, 50)));
}

const emptyProfile: UserProfile = {
  uid: 'guest',
  email: null,
  displayName: null,
  photoURL: null,
  favorites: [],
  bookmarks: [],
  watchHistory: [],
  createdAt: Date.now(),
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Load profile from Firestore or localStorage
  const loadProfile = useCallback(async (firebaseUser: FirebaseUser | null) => {
    if (firebaseUser) {
      // Try Firestore first
      await import('@firebase/firestore');
      const ref = doc(db, 'users', firebaseUser.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setProfile(snap.data() as UserProfile);
      } else {
        // Create default profile
        const newProfile: UserProfile = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          favorites: [],
          bookmarks: [],
          watchHistory: [],
          createdAt: Date.now(),
        };
        await setDoc(ref, newProfile);
        setProfile(newProfile);
      }
    } else {
      // Guest mode: load from localStorage
      setProfile({
        ...emptyProfile,
        favorites: getLocalFavorites(),
        bookmarks: getLocalBookmarks(),
        watchHistory: getLocalWatch(),
      });
    }
  }, []);

  // Subscribe to Firebase auth state
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    try {
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        setUser(firebaseUser);
        await loadProfile(firebaseUser);
        setLoading(false);
      });
    } catch (e) {
      console.warn('Firebase auth unavailable:', e);
      // Try local auth fallback
      const localUser = localStorage.getItem('anime_local_user');
      if (localUser) {
        try {
          setUser(JSON.parse(localUser));
        } catch {}
      }
      setProfile({
        ...emptyProfile,
        favorites: getLocalFavorites(),
        bookmarks: getLocalBookmarks(),
        watchHistory: getLocalWatch(),
      });
      setLoading(false);
    }
    return () => { unsubscribe?.(); };
  }, [loadProfile]);

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const register = async (email: string, password: string, displayName: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(userCredential.user, { displayName });
  };

  const logout = async () => {
    await firebaseSignOut(auth);
  };

  const updateProfileData = async (data: Partial<{ displayName: string; photoURL: string }>) => {
    if (user) {
      await updateProfile(user, data);
    }
  };

  // Sync localStorage changes for guests
  const syncLocal = (updatedProfile: UserProfile) => {
    saveLocalFavorites(updatedProfile.favorites);
    saveLocalBookmarks(updatedProfile.bookmarks);
    saveLocalWatch(updatedProfile.watchHistory);
    setProfile(updatedProfile);
  };

  const toggleFavorite = useCallback((animeId: number) => {
    setProfile(prev => {
      if (!prev) return prev;
      const exists = prev.favorites.includes(animeId);
      const updated: UserProfile = {
        ...prev,
        favorites: exists
          ? prev.favorites.filter(id => id !== animeId)
          : [...prev.favorites, animeId],
      };
      syncLocal(updated);
      // Also sync to Firestore if logged in
      if (user) {
        const ref = doc(db, 'users', user.uid);
        setDoc(ref, updated, { merge: true }).catch(() => {});
      }
      return updated;
    });
  }, [user]);

  const toggleBookmark = useCallback((animeId: number) => {
    setProfile(prev => {
      if (!prev) return prev;
      const exists = prev.bookmarks.includes(animeId);
      const updated: UserProfile = {
        ...prev,
        bookmarks: exists
          ? prev.bookmarks.filter(id => id !== animeId)
          : [...prev.bookmarks, animeId],
      };
      syncLocal(updated);
      if (user) {
        const ref = doc(db, 'users', user.uid);
        setDoc(ref, updated, { merge: true }).catch(() => {});
      }
      return updated;
    });
  }, [user]);

  const favoriteExists = useCallback((animeId: number) => {
    return profile?.favorites.includes(animeId) ?? false;
  }, [profile?.favorites]);

  const bookmarkExists = useCallback((animeId: number) => {
    return profile?.bookmarks.includes(animeId) ?? false;
  }, [profile?.bookmarks]);

  const updateWatchProgress = useCallback((item: Omit<ContinueWatchItem, 'timestamp'>) => {
    setProfile(prev => {
      if (!prev) return prev;
      const existing = prev.watchHistory.findIndex(w => w.id === item.id);
      const newItem: ContinueWatchItem = { ...item, timestamp: Date.now() };
      const watchHistory = existing >= 0
        ? prev.watchHistory.map((w, i) => i === existing ? newItem : w)
        : [newItem, ...prev.watchHistory];
      const updated: UserProfile = { ...prev, watchHistory };
      syncLocal(updated);
      if (user) {
        const ref = doc(db, 'users', user.uid);
        setDoc(ref, updated, { merge: true }).catch(() => {});
      }
      return updated;
    });
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      loading,
      login,
      register,
      logout,
      updateProfile: updateProfileData,
      toggleFavorite,
      toggleBookmark,
      favoriteExists,
      bookmarkExists,
      updateWatchProgress,
    }}>
      {children}
    </AuthContext.Provider>
  );
};